import React, {CSSProperties, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {InfoWindow, Map as GoogleMap, useMap, useMapsLibrary} from '@vis.gl/react-google-maps';
import {MarkerClusterer, Cluster, ClusterStats} from '@googlemaps/markerclusterer';
import {RegionalPartner} from "../Interfaces/RegionalPartner";

const containerStyles = {
    width: '100%',
    height: '845px'
};

type Props = {
    data: RegionalPartner[]
    zoom: number
    containerStyles?: CSSProperties | undefined
}

// Separate component for markers with clustering
const ClusteredMarkers: React.FC<{
    data: RegionalPartner[],
    onMarkerClick: (id: number) => void
}> = ({data, onMarkerClick}) => {
    const map = useMap();
    const markerLib = useMapsLibrary('marker');
    const clustererRef = useRef<MarkerClusterer | null>(null);
    const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);

    // Initialize clusterer
    useEffect(() => {
        if (!map || !markerLib) return;
        
        if (!clustererRef.current) {
            clustererRef.current = new MarkerClusterer({
                map,
                renderer: {
                    render: ({count, position}: Cluster, stats: ClusterStats) => {
                        let color = '#4CAF50';
                        let textColor = '#fff';
                        
                        if (count > 20) {
                            color = '#FF9800';
                        } else if (count > 10) {
                            color = '#FFC107';
                            textColor = '#000';
                        }

                        const div = document.createElement('div');
                        div.style.cssText = `
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            width: 40px;
                            height: 40px;
                            border-radius: 50%;
                            background-color: ${color};
                            color: ${textColor};
                            font-size: 14px;
                            font-weight: bold;
                            border: 2px solid #fff;
                            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                        `;
                        div.textContent = String(count);

                        return new markerLib.AdvancedMarkerElement({
                            position,
                            zIndex: 1000 + count,
                            content: div,
                        });
                    }
                }
            });
        }

        return () => {
            if (clustererRef.current) {
                clustererRef.current.clearMarkers();
                clustererRef.current = null;
            }
            markersRef.current = [];
        };
    }, [map, markerLib]);

    // Create and manage markers when data changes
    useEffect(() => {
        if (!map || !markerLib || !clustererRef.current) return;

        // Clear existing markers
        clustererRef.current.clearMarkers();
        markersRef.current.forEach(marker => {
            marker.map = null;
        });
        markersRef.current = [];

        // Create new markers
        const newMarkers = data.map(partner => {
            // Create custom pin content
            const pinContent = document.createElement('div');
            pinContent.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="38" viewBox="0 0 28 38">
                    <path fill="#E53935" stroke="#B71C1C" stroke-width="1" d="M14 0C6.268 0 0 6.268 0 14c0 7.732 14 24 14 24s14-16.268 14-24C28 6.268 21.732 0 14 0z"/>
                    <circle fill="#fff" cx="14" cy="14" r="5"/>
                </svg>
            `;
            pinContent.style.cursor = 'pointer';

            const marker = new markerLib.AdvancedMarkerElement({
                position: {lat: partner.latitude, lng: partner.longitude},
                content: pinContent,
            });

            marker.addListener('gmp-click', () => {
                onMarkerClick(partner.id);
            });

            return marker;
        });

        markersRef.current = newMarkers;
        clustererRef.current.addMarkers(newMarkers);

        // Fit bounds
        if (data.length > 0) {
            const bounds = new google.maps.LatLngBounds();
            data.forEach(partner => {
                bounds.extend({lat: partner.latitude, lng: partner.longitude});
            });
            map.fitBounds(bounds);
        }

        return () => {
            // Cleanup listeners
            markersRef.current.forEach(marker => {
                google.maps.event.clearInstanceListeners(marker);
            });
        };
    }, [map, markerLib, data, onMarkerClick]);

    return null;
};

const Map: React.FC<Props> = (props) => {
    const [activeMarkerId, setActiveMarkerId] = useState<number | null>(null);
    
    const mapId = document.getElementById('root')?.dataset.mapId || '';

    const handleMarkerClick = useCallback((id: number) => {
        setActiveMarkerId(id);
    }, []);

    const handleInfoWindowClose = useCallback(() => {
        setActiveMarkerId(null);
    }, []);

    const activePartner = useMemo(
        () => activeMarkerId ? props.data.find(p => p.id === activeMarkerId) : null,
        [props.data, activeMarkerId]
    );

    return (
        <GoogleMap
            style={props.containerStyles ? props.containerStyles : containerStyles}
            defaultZoom={props.zoom}
            defaultCenter={{lat: 46.8182, lng: 8.2275}}
            mapId={mapId}
        >
            <ClusteredMarkers 
                data={props.data} 
                onMarkerClick={handleMarkerClick}
            />

            {activeMarkerId && activePartner && (
                <InfoWindow
                    position={{lat: activePartner.latitude, lng: activePartner.longitude}}
                    onCloseClick={handleInfoWindowClose}
                >
                    <div className={"info-window-data"}>
                        <h3 className={"title"}>{activePartner.title}</h3>

                        <div className={"address"}>
                            {activePartner.address}
                        </div>
                        <div className={"zip-city"}>
                            CH-{activePartner.zip} {activePartner.city}
                        </div>

                        <hr/>
                        {activePartner.phone && (
                            <div className={"phone"}>T&nbsp;
                                <a href={"tel:" + activePartner.phone.replace(/\s/g, '')}>
                                    {activePartner.phone}
                                </a>
                            </div>
                        )}

                        {activePartner.email && (
                            <div className={"email"}>E&nbsp;
                                <a href={"mailto:" + activePartner.email}>
                                    {activePartner.email}
                                </a>
                            </div>
                        )}
                    </div>
                </InfoWindow>
            )}
        </GoogleMap>
    );
}

export default Map
